// PracticalGuideStudyingAbroad.js
import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, BookOpen, Target, CheckCircle, AlertCircle } from 'lucide-react';

const PracticalGuideStudyingAbroad = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Image */}
      <section className="relative py-20 bg-gradient-to-br from-[#202356] to-[#2542D4]">
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-30"
            style={{ 
              backgroundImage: `url(${require('../../assets/blog-1.jpg')})`,
            }}
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-white"
          >
            <Link 
              to="/blog" 
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition"
            >
              <ArrowLeft size={20} />
              Back to Blog
            </Link>
            
            <div className="flex items-center gap-4 text-sm font-light mb-6">
              <div className="flex items-center gap-2">
                <Calendar size={18} />
                <span>March 10, 2024</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={18} />
                <span>8 min read</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-semibold mb-6 leading-tight">
              A Practical Guide to Studying Abroad: From Research to Admission
            </h1>
            
            <p className="text-xl font-light leading-relaxed text-white/90">
              Choosing the right country, university, and course can feel overwhelming. This comprehensive guide breaks down the key steps involved in studying abroad, from initial research and consultations to preparing documents and submitting strong applications.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="prose prose-lg max-w-none"
          >
            {/* Introduction */}
            <div className="mb-12">
              <h2 className="text-3xl font-semibold text-[#202356] mb-4">Understanding the Study Abroad Journey</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Studying abroad is a transformative experience that opens doors to global opportunities, cultural immersion, and academic excellence. However, the process can be complex and competitive, requiring careful planning and preparation. With thousands of universities and courses available across multiple countries, making informed decisions becomes crucial for success.
              </p>
              <p className="text-gray-600 leading-relaxed">
                This guide will walk you through each stage of the study abroad process, helping you navigate the challenges and make decisions that align with your academic goals, career aspirations, and personal circumstances.
              </p>
            </div>

            {/* Phase 1: Research and Planning */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#F5F8FA] rounded-lg flex items-center justify-center">
                  <BookOpen className="text-[#202356]" size={24} />
                </div>
                <h2 className="text-3xl font-semibold text-[#202356]">Phase 1: Research and Planning</h2>
              </div>
              
              <h3 className="text-xl font-semibold text-[#202356] mb-4">Self-Assessment and Goal Setting</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Before diving into university research, take time to assess your academic strengths, career goals, and personal preferences. Consider:
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Academic Performance:</strong> Your grades, test scores, and academic achievements
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Career Goals:</strong> How studying abroad aligns with your future career plans
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Budget Considerations:</strong> Tuition fees, living costs, and available funding
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Personal Preferences:</strong> Climate, culture, language, and lifestyle factors
                  </div>
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-[#202356] mb-4">Country and University Research</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Research should be systematic and thorough. Start by identifying countries known for excellence in your field of study, then narrow down to specific universities. Key factors to consider:
              </p>
              
              <div className="bg-gray-50 p-6 rounded-lg mb-8">
                <h4 className="font-semibold text-[#202356] mb-4">Top Research Factors:</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-medium text-gray-800 mb-2">Academic Factors</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• University rankings and reputation</li>
                      <li>• Program accreditation and recognition</li>
                      <li>• Faculty expertise and research opportunities</li>
                      <li>• Course curriculum and flexibility</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-medium text-gray-800 mb-2">Practical Factors</h5>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Tuition fees and scholarship opportunities</li>
                      <li>• Cost of living and accommodation options</li>
                      <li>• Post-study work opportunities</li>
                      <li>• Student visa requirements and policies</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Phase 2: Consultation and Shortlisting */}
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#F5F8FA] rounded-lg flex items-center justify-center">
                  <Target className="text-[#202356]" size={24} />
                </div>
                <h2 className="text-3xl font-semibold text-[#202356]">Phase 2: Consultation and Shortlisting</h2>
              </div>
              
              <h3 className="text-xl font-semibold text-[#202356] mb-4">Professional Consultation Benefits</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                While independent research is valuable, professional consultation can provide insights that aren't readily available online. Education consultants offer:
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Insider Knowledge:</strong> Information about admission trends and requirements
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Realistic Assessment:</strong> Honest evaluation of your admission chances
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Strategic Planning:</strong> Help with creating a balanced university list
                  </div>
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-[#202356] mb-4">Creating Your University Shortlist</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                A well-balanced shortlist typically includes 8-12 universities across different categories:
              </p>
              
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-[#202356] mb-2">Dream Schools (2-3)</h4>
                  <p className="text-sm text-gray-600">Top-tier universities where admission is competitive but achievable</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-[#202356] mb-2">Target Schools (4-6)</h4>
                  <p className="text-sm text-gray-600">Universities where your profile matches typical admission criteria</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-[#202356] mb-2">Safety Schools (2-3)</h4>
                  <p className="text-sm text-gray-600">Universities where you have a high chance of admission</p>
                </div>
              </div>
            </div>

            {/* Phase 3: Document Preparation */}
            <div className="mb-12">
              <h2 className="text-3xl font-semibold text-[#202356] mb-6">Phase 3: Document Preparation</h2>
              
              <h3 className="text-xl font-semibold text-[#202356] mb-4">Essential Documents</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Document preparation is critical and should begin well in advance. Each university may have specific requirements, but most applications include:
              </p>
              
              <div className="space-y-6 mb-8">
                <div className="border-l-4 border-[#202356] pl-6">
                  <h4 className="font-semibold text-gray-800 mb-2">Academic Transcripts</h4>
                  <p className="text-gray-600">Official transcripts from all previous educational institutions, with certified translations if required.</p>
                </div>
                <div className="border-l-4 border-[#202356] pl-6">
                  <h4 className="font-semibold text-gray-800 mb-2">Standardized Test Scores</h4>
                  <p className="text-gray-600">TOEFL/IELTS for English proficiency, SAT/ACT for undergraduate programs, GRE/GMAT for graduate programs.</p>
                </div>
                <div className="border-l-4 border-[#202356] pl-6">
                  <h4 className="font-semibold text-gray-800 mb-2">Personal Statement</h4>
                  <p className="text-gray-600">A compelling essay that highlights your academic interests, career goals, and why you're a good fit for the program.</p>
                </div>
                <div className="border-l-4 border-[#202356] pl-6">
                  <h4 className="font-semibold text-gray-800 mb-2">Letters of Recommendation</h4>
                  <p className="text-gray-600">Strong recommendations from professors or employers who can speak to your abilities and potential.</p>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg mb-8">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Pro Tip:</h4>
                    <p className="text-gray-600">Start document preparation at least 6-8 months before application deadlines. This gives you time to retake tests if needed and gather strong recommendations.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Phase 4: Application Submission */}
            <div className="mb-12">
              <h2 className="text-3xl font-semibold text-[#202356] mb-6">Phase 4: Application Submission</h2>
              
              <h3 className="text-xl font-semibold text-[#202356] mb-4">Application Timeline</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Most universities have specific application windows, and timing can significantly impact your admission chances:
              </p>
              
              <div className="bg-gray-50 p-6 rounded-lg mb-8">
                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <span className="font-medium text-gray-800">Early Decision/Early Action</span>
                    <span className="text-sm text-gray-600">October - November</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <span className="font-medium text-gray-800">Regular Decision</span>
                    <span className="text-sm text-gray-600">January - March</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-800">Rolling Admissions</span>
                    <span className="text-sm text-gray-600">Varies by institution</span>
                  </div>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-[#202356] mb-4">Final Review Process</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                Before submitting any application, conduct a thorough review:
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Double-check all deadlines</strong> and submission requirements
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Proofread all documents</strong> for grammar and spelling errors
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Verify application fees</strong> and payment methods
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Save confirmation receipts</strong> for all submitted applications
                  </div>
                </li>
              </ul>
            </div>

            {/* Post-Application Phase */}
            <div className="mb-12">
              <h2 className="text-3xl font-semibold text-[#202356] mb-6">Post-Application: What Comes Next?</h2>
              
              <h3 className="text-xl font-semibold text-[#202356] mb-4">Waiting Period</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                After submitting applications, the waiting period can be stressful. Use this time productively:
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Research visa requirements</strong> for your preferred countries
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Explore scholarship opportunities</strong> and financial aid options
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-gray-600 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <strong className="text-gray-800">Prepare for interviews</strong> if required by your chosen programs
                  </div>
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-[#202356] mb-4">Making Your Decision</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                When admission offers arrive, consider multiple factors beyond rankings:
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="font-semibold text-[#202356] mb-3">Academic Factors</h4>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li>• Program curriculum and specializations</li>
                    <li>• Research opportunities and faculty expertise</li>
                    <li>• Class sizes and student-to-faculty ratio</li>
                    <li>• Internship and practical experience opportunities</li>
                  </ul>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="font-semibold text-[#202356] mb-3">Personal Factors</h4>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li>• Location and campus environment</li>
                    <li>• Cost and financial aid packages</li>
                    <li>• Student diversity and support services</li>
                    <li>• Career placement and alumni network</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Conclusion */}
            <div className="mb-12">
              <h2 className="text-3xl font-semibold text-[#202356] mb-6">Conclusion: Your Journey Begins Here</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Studying abroad is a significant investment in your future, but with careful planning and preparation, it can be one of the most rewarding experiences of your life. Remember that the process is a marathon, not a sprint – take it one step at a time, seek guidance when needed, and stay focused on your goals.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                Whether you're just beginning your research or ready to submit applications, professional guidance can make the difference between a stressful experience and a successful journey. Consider booking a consultation to get personalized advice for your specific situation.
              </p>
              
              <div className="bg-gradient-to-r from-[#202356] to-[#2542D4] p-8 rounded-lg text-white">
                <h3 className="text-2xl font-semibold mb-4">Ready to Start Your Study Abroad Journey?</h3>
                <p className="mb-6 text-white/90">
                  Our experienced consultants can help you navigate every step of the process, from university selection to application submission and beyond.
                </p>
                <button className="bg-white text-[#202356] px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition">
                  Book a Free Consultation
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PracticalGuideStudyingAbroad;
